import type { ComponentProps, ReactNode } from 'react'
import { cn } from '../lib/cn'
import { Card } from './card'
export function Stat({
  className,
  appearance = 'compact',
  ...props
}: ComponentProps<'div'> & { appearance?: 'compact' | 'card' }) {
  return appearance === 'card' ? (
    <Card>
      <div {...props} data-slot="stat" className={cn('space-y-1', className)} />
    </Card>
  ) : (
    <div {...props} data-slot="stat" className={cn('space-y-1', className)} />
  )
}
export function StatLabel({ className, ...props }: ComponentProps<'p'>) {
  return <p {...props} className={cn('text-xs font-medium text-muted-foreground', className)} />
}
export function StatValue({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p {...props} className={cn('text-2xl font-semibold tracking-tight tabular-nums', className)} />
  )
}
export function StatDescription({ className, ...props }: ComponentProps<'p'>) {
  return <p {...props} className={cn('text-xs text-muted-foreground', className)} />
}

export function StatOverview({
  label,
  value,
  description,
  icon,
  children,
  className,
  headerAppearance = 'subtle',
  ...props
}: ComponentProps<typeof Card> & {
  label: ReactNode
  value: ReactNode
  description?: ReactNode
  icon?: ReactNode
  headerAppearance?: 'subtle' | 'accent'
}) {
  return (
    <Card {...props} padding="none" className={cn('overflow-hidden', className)}>
      <div
        className={cn(
          'flex items-center justify-between gap-4 px-5 py-4 sm:px-6',
          headerAppearance === 'accent' ? 'bg-primary text-primary-foreground' : 'bg-accent/35',
        )}
      >
        <div className="min-w-0">
          <p
            className={cn(
              'text-sm',
              headerAppearance === 'accent'
                ? 'text-primary-foreground/85'
                : 'text-accent-foreground',
            )}
          >
            {label}
          </p>
          <strong className="mt-1 block text-2xl tabular-nums sm:text-3xl">{value}</strong>
          {description ? (
            <p
              className={cn(
                'mt-1 text-sm',
                headerAppearance === 'accent' ? 'text-primary-foreground/85' : 'text-foreground/75',
              )}
            >
              {description}
            </p>
          ) : null}
        </div>
        {icon ? (
          <span
            aria-hidden="true"
            className="grid size-11 shrink-0 place-items-center rounded-full bg-card text-action shadow-xs"
          >
            {icon}
          </span>
        ) : null}
      </div>
      <div className="grid gap-7 p-5 sm:p-6 lg:grid-cols-2 lg:gap-0">{children}</div>
    </Card>
  )
}

export function StatOverviewSection({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      className={cn(
        'min-w-0 border-t border-border pt-7 first:border-t-0 first:pt-0 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-7 lg:first:border-l-0 lg:first:pr-7 lg:first:pl-0',
        className,
      )}
    />
  )
}

const progressTones = {
  accent: 'bg-action',
  success: 'bg-success-foreground',
  warning: 'bg-warning-foreground',
}

export function StatProgress({
  value,
  max,
  tone = 'accent',
  remainderTone,
  className,
  ...props
}: Omit<ComponentProps<'div'>, 'children'> & {
  value: number
  max: number
  tone?: keyof typeof progressTones
  remainderTone?: keyof typeof progressTones
  'aria-label': string
}) {
  const maximum = Number.isFinite(max) && max > 0 ? max : 0
  const current = maximum > 0 && Number.isFinite(value) ? Math.min(Math.max(value, 0), maximum) : 0
  const percentage = maximum > 0 ? (current / maximum) * 100 : 0
  return (
    <div
      {...props}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={maximum || 1}
      aria-valuenow={current}
      className={cn('mt-3 flex h-3 overflow-hidden rounded-full bg-muted', className)}
    >
      <span
        aria-hidden="true"
        className={cn('h-full shrink-0 motion-safe:transition-[width]', progressTones[tone])}
        style={{ width: `${percentage}%` }}
      />
      {remainderTone && maximum > 0 ? (
        <span
          aria-hidden="true"
          className={cn(
            'h-full shrink-0 motion-safe:transition-[width]',
            progressTones[remainderTone],
          )}
          style={{ width: `${100 - percentage}%` }}
        />
      ) : null}
    </div>
  )
}
